import { useState } from "react";

import { Add, Delete, Save } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AlertCrimeTypeFullResource,
  createCrimeType,
  deleteCrimeType,
  getCrimeTypesAdmin,
  patchCrimeType,
} from "@rubi/api-generated";
import { toast } from "@rubi/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";

const GROUP_LABELS: Record<string, string> = {
  person_crime: "Person Crime",
  property_crime: "Property Crime",
  robbery_crime: "Robbery Crime",
  organized_crime: "Organized Crime",
  drugs_crime: "Drugs Crime",
  computer_crime: "Computer Crime",
  other_crime: "Other Crime",
};

type RowEdit = { name_en: string; name_fr: string };

type NewRow = {
  key: string;
  group_key: string;
  name_en: string;
  name_fr: string;
};

const EMPTY_NEW: NewRow = {
  key: "",
  group_key: "person_crime",
  name_en: "",
  name_fr: "",
};

export default function AlertCrimeTypeSettings() {
  const queryClient = useQueryClient();

  const { data: crimes = [], isLoading } = useQuery({
    queryKey: ["admin-crime-types"],
    queryFn: async () => {
      const res = await getCrimeTypesAdmin();
      return res.data ?? [];
    },
  });

  const [edits, setEdits] = useState<Record<number, RowEdit>>({});
  const [saving, setSaving] = useState<Record<number, boolean>>({});
  const [deleteTarget, setDeleteTarget] =
    useState<AlertCrimeTypeFullResource | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [newRow, setNewRow] = useState<NewRow | null>(null);
  const [creating, setCreating] = useState(false);

  const getEdit = (row: AlertCrimeTypeFullResource): RowEdit =>
    edits[row.id] ?? { name_en: row.name_en, name_fr: row.name_fr };

  const handleChange = (
    row: AlertCrimeTypeFullResource,
    field: keyof RowEdit,
    value: string,
  ) => {
    setEdits((prev) => {
      const current = prev[row.id] ?? {
        name_en: row.name_en,
        name_fr: row.name_fr,
      };
      return { ...prev, [row.id]: { ...current, [field]: value } };
    });
  };

  const handleSave = async (row: AlertCrimeTypeFullResource) => {
    const edit = edits[row.id];
    if (!edit) return;
    const body: { name_en?: string; name_fr?: string } = {};
    if (edit.name_en !== row.name_en) body.name_en = edit.name_en;
    if (edit.name_fr !== row.name_fr) body.name_fr = edit.name_fr;
    if (!Object.keys(body).length) return;

    setSaving((prev) => ({ ...prev, [row.id]: true }));
    try {
      await patchCrimeType({ path: { crime_id: row.id }, body });
      await queryClient.invalidateQueries({ queryKey: ["admin-crime-types"] });
      await queryClient.invalidateQueries({ queryKey: ["alert-crime-types"] });
      setEdits((prev) => {
        const n = { ...prev };
        delete n[row.id];
        return n;
      });
      toast.success(`Saved "${row.key}"`);
    } catch {
      toast.error(`Failed to save "${row.key}"`);
    } finally {
      setSaving((prev) => ({ ...prev, [row.id]: false }));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteCrimeType({ path: { crime_id: deleteTarget.id } });
      await queryClient.invalidateQueries({ queryKey: ["admin-crime-types"] });
      await queryClient.invalidateQueries({ queryKey: ["alert-crime-types"] });
      toast.success(`Deleted "${deleteTarget.key}"`);
      setDeleteTarget(null);
    } catch {
      toast.error(`Failed to delete "${deleteTarget.key}"`);
    } finally {
      setDeleting(false);
    }
  };

  const handleCreate = async () => {
    if (!newRow || !newRow.key || !newRow.name_en || !newRow.name_fr) {
      toast.error("All fields are required");
      return;
    }
    setCreating(true);
    try {
      await createCrimeType({ body: newRow });
      await queryClient.invalidateQueries({ queryKey: ["admin-crime-types"] });
      await queryClient.invalidateQueries({ queryKey: ["alert-crime-types"] });
      toast.success(`Created "${newRow.key}"`);
      setNewRow(null);
    } catch {
      toast.error(`Failed to create "${newRow.key}"`);
    } finally {
      setCreating(false);
    }
  };

  const isDirty = (row: AlertCrimeTypeFullResource) => {
    const e = edits[row.id];
    return !!e && (e.name_en !== row.name_en || e.name_fr !== row.name_fr);
  };

  const grouped = crimes.reduce<Record<string, AlertCrimeTypeFullResource[]>>(
    (acc, c) => {
      (acc[c.group_key] ??= []).push(c);
      return acc;
    },
    {},
  );

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h5" fontWeight={600}>
          Crime Types
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setNewRow(EMPTY_NEW)}
        >
          Add Crime Type
        </Button>
      </Box>

      <Stack spacing={3}>
        {Object.entries(grouped).map(([groupKey, rows]) => (
          <Paper key={groupKey} variant="outlined">
            <Box px={2} py={1.5} display="flex" alignItems="center" gap={1}>
              <Typography variant="subtitle1" fontWeight={600}>
                {GROUP_LABELS[groupKey] ?? groupKey}
              </Typography>
              <Chip label={rows.length} size="small" />
            </Box>
            <Divider />
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 260, color: "text.secondary" }}>
                      Key
                    </TableCell>
                    <TableCell sx={{ color: "text.secondary" }}>
                      English
                    </TableCell>
                    <TableCell sx={{ color: "text.secondary" }}>
                      French
                    </TableCell>
                    <TableCell sx={{ width: 96 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => {
                    const edit = getEdit(row);
                    return (
                      <TableRow key={row.id} hover>
                        <TableCell>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            fontFamily="monospace"
                          >
                            {row.key}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            value={edit.name_en}
                            onChange={(e) =>
                              handleChange(row, "name_en", e.target.value)
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            value={edit.name_fr}
                            onChange={(e) =>
                              handleChange(row, "name_fr", e.target.value)
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={0.5}>
                            <Tooltip title="Save changes">
                              <span>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  disabled={!isDirty(row) || saving[row.id]}
                                  onClick={() => handleSave(row)}
                                >
                                  {saving[row.id] ? (
                                    <CircularProgress size={18} />
                                  ) : (
                                    <Save fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => setDeleteTarget(row)}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ))}
      </Stack>

      {/* Create dialog */}
      <Dialog
        open={!!newRow}
        onClose={() => setNewRow(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Crime Type</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Key (snake_case)"
              size="small"
              fullWidth
              value={newRow?.key ?? ""}
              onChange={(e) =>
                setNewRow((p) => p && { ...p, key: e.target.value })
              }
              placeholder="e.g. extortion"
            />
            <Select
              size="small"
              fullWidth
              value={newRow?.group_key ?? "person_crime"}
              onChange={(e) =>
                setNewRow((p) => p && { ...p, group_key: e.target.value })
              }
            >
              {Object.entries(GROUP_LABELS).map(([k, label]) => (
                <MenuItem key={k} value={k}>
                  {label}
                </MenuItem>
              ))}
            </Select>
            <TextField
              label="English name"
              size="small"
              fullWidth
              value={newRow?.name_en ?? ""}
              onChange={(e) =>
                setNewRow((p) => p && { ...p, name_en: e.target.value })
              }
            />
            <TextField
              label="French name"
              size="small"
              fullWidth
              value={newRow?.name_fr ?? ""}
              onChange={(e) =>
                setNewRow((p) => p && { ...p, name_fr: e.target.value })
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewRow(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={creating}
          >
            {creating ? <CircularProgress size={18} /> : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Crime Type</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete <strong>{deleteTarget?.key}</strong>? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={18} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
